<template>
  <a-modal
    :title="title"
    width="880px"
    :open="open"
    :bodyStyle="{ maxHeight: '78vh', overflowY: 'auto', padding: '0 24px' }"
    :confirm-loading="loading"
    cancel-text="取消"
    ok-text="确定"
    centered
    destroy-on-close
    @cancel="handleCancel"
    @ok="handleOk"
  >
    <a-form
      ref="formRef"
      :model="model"
      layout="vertical"
      size="small"
    >
      <a-divider orientation="left">基本信息</a-divider>
      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-item
            label="数据源名称"
            name="name"
            :rules="rules.name"
          >
            <a-input
              v-model:value="model.name"
              placeholder="请填写数据源名称"
              allow-clear
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item
            label="数据库类型"
            name="db_type"
            :rules="rules.db_type"
          >
            <a-select
              v-model:value="model.db_type"
              placeholder="请选择"
            >
              <a-select-option
                v-for="opt in dbTypeOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
      </a-row>

      <a-divider orientation="left">数据库连接信息</a-divider>
      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-item
            label="数据库主机"
            name="db_host"
            :rules="rules.db_host"
          >
            <a-input
              v-model:value="model.db_host"
              placeholder="如 127.0.0.1"
              allow-clear
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item
            label="数据库端口"
            name="db_port"
            :rules="rules.db_port"
          >
            <a-input-number
              v-model:value="model.db_port"
              style="width: 100%"
              :min="1"
              :max="65535"
              placeholder="如 3306"
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item
            label="数据库用户"
            name="db_user"
            :rules="rules.db_user"
          >
            <a-input
              v-model:value="model.db_user"
              placeholder="如 root"
              allow-clear
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item
            label="数据库密码"
            name="db_password"
          >
            <a-input-password
              v-model:value="model.db_password"
              placeholder="修改时留空则不修改密码"
              allow-clear
              autocomplete="new-password"
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item
            :label="db_name_field_lable"
            name="db_name"
            :rules="rules.db_name"
          >
            <a-input
              v-model:value="model.db_name"
              placeholder="如 my_db"
              allow-clear
            />
          </a-form-item>
        </a-col>
        <a-col
          v-if="showSchemaField"
          :span="12"
        >
          <a-form-item label="默认Schema">
            <a-input
              v-model:value="model.db_schema"
              allow-clear
            />
          </a-form-item>
        </a-col>
      </a-row>
      <a-form-item
        label="数据库选项"
        name="db_options"
      >
        <JsonStr
          v-model:value="model.db_options"
          placeholder='可选，JSON 对象，如 {"charset":"utf8mb4"}'
        />
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { cloneDeep } from 'lodash';
import { message } from 'ant-design-vue';
import { computed, onMounted, reactive, ref } from 'vue';
import { event_obj } from 'liyu-pc-base/init-plugin';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { useUserStore } from 'liyu-pc-base/init-plugin';
import JsonStr from 'liyu-pc-base/components/FormRender/JsonStr/index.vue';

/* ★ v29.4：db_view 的「数据源管理」新增/编辑弹窗组件。
   移植 lysync syncDataSourceConfig，差异：DbDataSource 无 source_type/custom_func
   （全部是数据库型）；db_type 多 postgres；db_schema 是独立字段（非 db_options.schema）；
   详情/提交走 lybase 通用接口 get_/create_/update_db_data_source。
   连接测试不在此弹窗内 —— 由列表行【连接测试】菜单按钮（check_connect）承担。 */

interface Props {
  title: string;
  record: any;
}
const props = defineProps<Props>();
const emit = defineEmits(['cancel', 'ok']);

const formRef = ref<any>(null);
const loading = ref(false);
const open = ref(true);
const form_type = ref('create');
const userStore = useUserStore();
const current_org = userStore.currentOrg;
const db_name_field_lable = computed(() => {
  return model.value.db_type === 'oracle' ? 'SID/服务名' : '数据库名称';
});
/* oracle / postgres 有 schema 概念，显示独立字段 */
const showSchemaField = computed(() => ['oracle', 'postgres'].includes(model.value.db_type));

const dbTypeOptions = [
  { value: 'mysql', label: 'MySQL' },
  { value: 'tsql', label: 'SQL Server' },
  { value: 'oracle', label: 'Oracle' },
  { value: 'postgres', label: 'PostgreSQL' },
];

const model = ref<any>({
  name: undefined,
  db_type: 'mysql',
  db_host: undefined,
  db_port: undefined,
  db_user: undefined,
  db_password: undefined,
  db_name: undefined,
  db_schema: undefined,
  db_options: {},
});

const rules = reactive<any>({
  name: [{ required: true, message: '请填写数据源名称' }],
  db_type: [{ required: true, message: '请选择数据库类型' }],
  db_host: [{ required: true, message: '请填写数据库主机' }],
  db_port: [{ required: true, message: '请填写数据库端口' }],
  db_user: [{ required: true, message: '请填写数据库用户' }],
  db_name: [{ required: true, message: '请填写数据库名称' }],
});

/* 兼容 getApiData 可能的返回结构（直接对象 或 {data:{...}}） */
const extractData = (res: any) => {
  if (res && typeof res === 'object' && !Array.isArray(res) && res.data !== undefined)
    return res.data;
  return res;
};

/* 编辑：调用公共项目提供的详情接口（<app_flag>/get_<model_name>，即 get_basemodel）
   获取完整 DbDataSource 记录。不依赖列表行数据（hide_json 剔除了 db_password，
   密码不回显，留空表示不修改）。参数名为 <model_name>_id，即 db_data_source_id。 */
const loadDetail = async (id: any) => {
  const res = await getApiData(
    { db_data_source_id: id, org_id: current_org?.id },
    `${props.record.model_app}/get_db_data_source`,
  );
  return extractData(res) || {};
};

/* 构建提交数据：始终 cloneDeep(model.value)，兜底默认值 */
const buildPayload = () => {
  const payload: any = cloneDeep(model.value);
  payload.update_field_list = [
    'name',
    'db_type',
    'db_host',
    'db_port',
    'db_user',
    'db_password',
    'db_name',
    'db_schema',
    'db_options',
  ];
  // 编辑时密码留空则不修改：删除字段值，并从 update_field_list 中剔除
  if (!payload.db_password && form_type.value === 'edit') {
    delete payload.db_password;
    payload.update_field_list = payload.update_field_list.filter(
      (k: string) => k !== 'db_password',
    );
  }
  return payload;
};

/* 提交：URL 遵循 /{app_flag}/create_{model} 与 /{app_flag}/update_{model} */
const handleOk = async () => {
  try {
    await formRef.value.validate();
    const payload = buildPayload();
    payload.db_data_source_id = props.record.id;
    const url =
      form_type.value === 'edit'
        ? `${props.record.model_app}/update_db_data_source`
        : `${props.record.model_app}/create_db_data_source`;
    if (form_type.value === 'edit') payload.id = props.record.id;
    loading.value = true;
    await getApiData({ ...payload, org_id: current_org?.id }, url);
    message.success('操作成功');
    event_obj()?.emit('refresh');
    emit('ok');
  } catch (error) {
    // 校验失败或接口异常：保持弹窗打开
  } finally {
    loading.value = false;
  }
};
const handleCancel = () => emit('cancel');

/* 初始化编辑：先调详情接口拿完整记录，再填充 */
const initForEdit = async () => {
  const id = props.record?.id;
  const record = (id != null ? await loadDetail(id) : props.record) || {};
  model.value = {
    name: record.name || '',
    db_type: record.db_type || 'mysql',
    db_host: record.db_host || undefined,
    db_port: record.db_port ?? undefined,
    db_user: record.db_user || undefined,
    db_password: undefined, // 密码不回显，留空表示不修改
    db_name: record.db_name || undefined,
    db_schema: record.db_schema || undefined,
    db_options: record.db_options,
  };
};

onMounted(async () => {
  if (props.record?.form_type) form_type.value = props.record.form_type;
  if (form_type.value === 'edit') await initForEdit();
});
</script>

<style scoped lang="less">
</style>
